// ── AR Flow — 📦 DEPÓSITO (tela própria do login do depósito) ────────────────
// Feita para o celular. O que chegou e precisa ser agendado aparece primeiro,
// em vermelho. O depósito liga para o cliente (pelo próprio celular), agenda a
// entrega, registra tentativas, marca como entregue e escreve as observações
// da entrega, que viram pendência para o consultor da venda retornar.
// Nada aqui manda mensagem pelo CRM nem altera o pedido no LIFT.

import { useCallback, useEffect, useRef, useState } from "react";
import { watchVisiblePolling } from "../visiblePolling.mjs";
import { VoiceDictationButton } from "../VoiceDictation";
import { enablePush, getPushStatus, pushSupported } from "../pushNotifications";

const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const DISPLAY = "'Bricolage Grotesque', 'Source Sans 3', system-ui, sans-serif";
const NOME_LOJA = { 1: "Rei dos Estofados", 2: "Rei dos Colchões", 3: "Mezzo Ponta Grossa", 4: "Mezzo Curitiba" };
const dm = v => (v ? v.slice(0, 10).split("-").reverse().slice(0, 2).join("/") : "");
const soDigitos = v => String(v || "").replace(/\D/g, "");
const zap = tel => { const d = soDigitos(tel); return d.length >= 10 ? `https://wa.me/${d.length <= 11 ? `55${d}` : d}` : null; };
const hojeSP = () => new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Sao_Paulo", year: "numeric", month: "2-digit", day: "2-digit",
}).format(new Date());

export async function chamarDeposito(url, corpo, options = {}) {
  const r = await fetch(url, corpo
    ? { method: "POST", credentials: "include", cache: "no-store", headers: { "Content-Type": "application/json" }, body: JSON.stringify(corpo), signal: options.signal }
    : { credentials: "include", cache: "no-store", signal: options.signal });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || "Não foi possível concluir agora.");
  if (corpo && d.ok !== true) throw new Error("O servidor não confirmou a gravação. Atualize a tela e tente novamente.");
  return d;
}

const GRUPOS = [
  { id: "agendar", titulo: "🔴 Chegou · ligar e agendar", cor: "#B42318", vazio: "Nada esperando agendamento. 👏" },
  { id: "atrasadas", titulo: "⏰ Agendamento vencido", cor: "#B42318", vazio: "Nenhuma entrega atrasada." },
  { id: "agendadas", titulo: "📅 Entregas agendadas", cor: "#1D4ED8", vazio: "Nenhuma entrega agendada." },
  { id: "aCaminho", titulo: "🚚 A caminho (na fábrica)", cor: "#B45309", vazio: "Nada a caminho." },
];

function Card({ th, item, meta, onFeito }) {
  const line = th.nuLine || th.sandDark;
  const [modo, setModo] = useState(null); // agendar | tentativa | obs | entregue
  const [f, setF] = useState({ data: "", periodo: "manha", horario: "", obs: "", resultado: "nao_atende", texto: "", tipo: "entrega", entregaData: "", entregaObs: "" });
  const [erro, setErro] = useState("");
  const [busy, setBusy] = useState(false);
  const requestRef = useRef(null);
  const mountedRef = useRef(true);
  const base = `/api/deposito/${item.storeId}/${encodeURIComponent(item.pedido)}`;
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestRef.current?.abort();
    };
  }, []);
  const enviar = async (url, corpo) => {
    if (requestRef.current) return;
    const controller = new AbortController();
    requestRef.current = controller;
    setBusy(true); setErro("");
    try {
      await chamarDeposito(url, corpo, { signal: controller.signal });
      if (!controller.signal.aborted && mountedRef.current) { setModo(null); onFeito(); }
    } catch (e) {
      if (!controller.signal.aborted && mountedRef.current) setErro(e.message);
    } finally {
      if (requestRef.current === controller) requestRef.current = null;
      if (mountedRef.current) setBusy(false);
    }
  };
  const campo = { width: "100%", boxSizing: "border-box", padding: "9px 10px", borderRadius: 4, border: `1px solid ${line}`, fontFamily: "inherit", fontSize: 15, marginTop: 6 };
  const botao = (primario) => ({ padding: "9px 12px", borderRadius: 4, border: primario ? "none" : `1px solid ${line}`, background: primario ? th.accent : "#fff", color: primario ? "#fff" : th.text, fontWeight: 700, fontSize: 14, cursor: busy ? "wait" : "pointer", fontFamily: "inherit", opacity: busy ? 0.6 : 1 });
  const ag = item.agendamento;
  const hoje = meta.hoje || hojeSP();
  const entregue = Boolean(item.entregueEm) || item.statusLift === "ENTREGUE";
  const abertas = item.observacoes.filter(o => o.pendencia === "aberta").length;
  const atualizarCampo = (campoNome, valor) => setF(atual => ({ ...atual, [campoNome]: valor }));
  return (
    <div style={{ background: "#fff", border: `1px solid ${line}`, borderRadius: 6, padding: "12px 12px 10px", marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline" }}>
        <strong style={{ fontSize: 16, color: th.text }}>{item.cliente.nome}</strong>
        <span style={{ fontFamily: MONO, fontSize: 12, color: th.muted }}>#{item.pedido}</span>
      </div>
      <div style={{ fontSize: 13, color: th.muted }}>{NOME_LOJA[item.storeId]}{item.consultor ? ` · vendedor: ${item.consultor}` : ""}{item.tipo === "pronta_entrega" ? " · pronta entrega" : ""}</div>
      {!entregue && item.provavelJaEntregue && <div style={{ display: "inline-block", marginTop: 6, padding: "4px 7px", borderRadius: 4, background: "#FEF3C7", color: "#92400E", fontSize: 12, fontWeight: 700 }}>Provável já entregue?</div>}
      <div style={{ fontSize: 14, color: th.text, marginTop: 4 }}>{item.itens.map(i => `${i.quantidade > 1 ? `${i.quantidade}× ` : ""}${i.produto}`).join(" · ")}</div>
      <div style={{ fontSize: 13, marginTop: 4, color: th.text }}>
        {item.chegouEm && <span>Chegou em {dm(item.chegouEm)}{item.diasNoDeposito > 0 ? ` (há ${item.diasNoDeposito} dia${item.diasNoDeposito > 1 ? "s" : ""})` : ""}. </span>}
        {!item.chegouEm && item.previsao && <span>Previsão do fornecedor: {dm(item.previsao)}. </span>}
        {ag && <span style={{ fontWeight: 700 }}>Entrega: {dm(ag.data)} · {meta.periodos?.[ag.periodo] || ag.periodo}{ag.horario ? ` ${ag.horario}` : ""}. </span>}
        {item.entregueEm && <span style={{ color: "#067647", fontWeight: 700 }}>Entregue em {dm(item.entregueEm)}. </span>}
      </div>
      {item.tentativas.length > 0 && <div style={{ fontSize: 12, color: th.muted, marginTop: 2 }}>Última ligação: {meta.resultadosTentativa?.[item.tentativas[0].resultado] || item.tentativas[0].resultado} ({dm(item.tentativas[0].em)}){item.tentativas.length > 1 ? ` · ${item.tentativas.length} tentativas` : ""}</div>}
      {item.observacoes.length > 0 && (
        <div style={{ marginTop: 6, background: th.sandDark, borderRadius: 4, padding: "6px 8px" }}>
          {item.observacoes.slice(0, 3).map(o => <div key={`${item.storeId}-${item.pedido}-${o.id}`} style={{ fontSize: 13, color: th.text }}>📝 {o.texto} <span style={{ color: th.muted, fontSize: 11 }}>({o.porNome}, {dm(o.em)}{o.pendencia === "resolvida" ? " · vendedor já retornou" : o.pendencia === "aberta" ? " · aguardando o vendedor" : ""})</span></div>)}
        </div>
      )}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
        {item.cliente.telefone && <a href={`tel:${soDigitos(item.cliente.telefone)}`} aria-disabled={busy} onClick={event => { if (busy) event.preventDefault(); }} style={{ ...botao(false), textDecoration: "none" }}>📞 Ligar</a>}
        {zap(item.cliente.telefone) && <a href={zap(item.cliente.telefone)} aria-disabled={busy} onClick={event => { if (busy) event.preventDefault(); }} target="_blank" rel="noreferrer" style={{ ...botao(false), textDecoration: "none" }}>💬 WhatsApp</a>}
        {!item.entregueEm && item.statusLift !== "ENTREGUE" && meta.grupo !== "aCaminho" && <button type="button" disabled={busy} style={botao(!ag)} onClick={() => setModo(modo === "agendar" ? null : "agendar")}>{ag ? "Reagendar" : "📅 Agendar"}</button>}
        {!item.entregueEm && meta.grupo !== "aCaminho" && <button type="button" disabled={busy} style={botao(false)} onClick={() => setModo(modo === "tentativa" ? null : "tentativa")}>Não consegui falar</button>}
        <button type="button" disabled={busy} style={botao(meta.grupo === "entregues" && abertas === 0 && item.observacoes.length === 0)} onClick={() => setModo(modo === "obs" ? null : "obs")}>📝 Observação</button>
        {!entregue && <button type="button" style={botao(false)} disabled={busy} onClick={() => {
          setErro("");
          setF(atual => ({ ...atual, entregaData: hoje, entregaObs: "" }));
          setModo(modo === "entregue" ? null : "entregue");
        }}>✅ Já foi entregue</button>}
      </div>
      {modo === "entregue" && (
        <form aria-label="Confirmar entrega do pedido" style={{ marginTop: 8 }} onSubmit={event => {
          event.preventDefault();
          if (!f.entregaData || f.entregaData > hoje || busy) return;
          enviar(`${base}/confirmar-entrega`, { data: f.entregaData, observacao: f.entregaObs.trim() });
        }}>
          <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: th.text }}>Quando o cliente recebeu ou levou o pedido?
            <input type="date" aria-label="Data da entrega realizada" required max={hoje} disabled={busy} value={f.entregaData} onChange={e => atualizarCampo("entregaData", e.target.value)} style={campo} />
          </label>
          <textarea aria-label="Observação da entrega realizada (opcional)" placeholder="Observação (opcional): cliente retirou na loja…" maxLength={2000} disabled={busy} value={f.entregaObs} onChange={e => atualizarCampo("entregaObs", e.target.value)} style={campo} />
          <div style={{ fontSize: 12, color: th.muted, marginTop: 5 }}>A data será usada no pós-venda do vendedor. Nenhuma mensagem será enviada ao cliente.</div>
          <button type="submit" disabled={busy || !f.entregaData || f.entregaData > hoje} style={{ ...botao(true), marginTop: 8 }}>Confirmar entrega</button>
          <button type="button" disabled={busy} style={{ ...botao(false), margin: "8px 0 0 6px" }} onClick={() => setModo(null)}>Cancelar</button>
        </form>
      )}
      {modo === "agendar" && (
        <div style={{ marginTop: 8 }}>
          <input type="date" aria-label="Data da entrega" value={f.data} min={meta.hoje} onChange={e => atualizarCampo("data", e.target.value)} style={campo} />
          <select aria-label="Período" value={f.periodo} onChange={e => atualizarCampo("periodo", e.target.value)} style={campo}>
            {Object.entries(meta.periodos || {}).map(([v, n]) => <option key={v} value={v}>{n}</option>)}
          </select>
          {f.periodo === "horario" && <input aria-label="Horário" placeholder="Ex.: 14h" value={f.horario} onChange={e => atualizarCampo("horario", e.target.value)} style={campo} />}
          <input aria-label="Observação do agendamento" placeholder="Observação (opcional): portaria, andar…" value={f.obs} onChange={e => atualizarCampo("obs", e.target.value)} style={campo} />
          <button type="button" disabled={busy || !f.data || (meta.hoje && f.data < meta.hoje) || (f.periodo === "horario" && !f.horario.trim())} style={{ ...botao(true), marginTop: 8, width: "100%" }} onClick={() => enviar(`${base}/agendar`, { data: f.data, periodo: f.periodo, horario: f.periodo === "horario" ? f.horario.trim() : "", obs: f.obs })}>Salvar agendamento</button>
        </div>
      )}
      {modo === "tentativa" && (
        <div style={{ marginTop: 8 }}>
          <select aria-label="O que aconteceu" value={f.resultado} onChange={e => atualizarCampo("resultado", e.target.value)} style={campo}>
            {Object.entries(meta.resultadosTentativa || {}).map(([v, n]) => <option key={v} value={v}>{n}</option>)}
          </select>
          <input aria-label="Detalhe" placeholder="Detalhe (opcional)" value={f.texto} onChange={e => atualizarCampo("texto", e.target.value)} style={campo} />
          <button type="button" disabled={busy} style={{ ...botao(true), marginTop: 8, width: "100%" }} onClick={() => enviar(`${base}/tentativa`, { resultado: f.resultado, texto: f.texto })}>Registrar tentativa</button>
        </div>
      )}
      {modo === "obs" && (
        <div style={{ marginTop: 8 }}>
          <select aria-label="Tipo da observação" value={f.tipo} onChange={e => atualizarCampo("tipo", e.target.value)} style={campo}>
            {(meta.tiposObservacao || []).map(t => <option key={t.id} value={t.id}>{t.rotulo}</option>)}
          </select>
          <textarea aria-label="Observação" rows={3} placeholder="Ex.: faltou o pé do sofá; cliente perguntou de mesa lateral; sala grande, pediu tapete…" value={f.texto} onChange={e => atualizarCampo("texto", e.target.value)} style={{ ...campo, resize: "vertical" }} />
          <VoiceDictationButton value={f.texto} onChange={v => setF({ ...f, texto: v })} />
          <div style={{ fontSize: 12, color: th.muted, marginTop: 4 }}>O vendedor da venda recebe um aviso para retornar ao cliente.</div>
          <button type="button" disabled={busy || !f.texto.trim()} style={{ ...botao(true), marginTop: 8, width: "100%" }} onClick={() => enviar(`${base}/observacao`, { tipo: f.tipo, texto: f.texto })}>Salvar observação</button>
        </div>
      )}
      {erro && <div role="alert" style={{ marginTop: 6, fontSize: 13, color: "#991B1B" }}>{erro}</div>}
    </div>
  );
}

export default function DepositoApp({ theme, currentUser, authenticatedUser, onSair, onMinhaConta }) {
  const th = theme;
  const identity = `${currentUser?.id ?? ""}:${currentUser?.role ?? ""}:${(currentUser?.storeIds || []).join(",")}`;
  const authenticatedIdentity = `${authenticatedUser?.id ?? ""}:${authenticatedUser?.role ?? ""}`;
  const isAuthenticatedDeposito = Number(authenticatedUser?.id) === 50 && authenticatedUser?.role === "deposito";
  const [resposta, setResposta] = useState(null);
  const [busca, setBusca] = useState("");
  const [aberto, setAberto] = useState({ agendar: true, atrasadas: true, agendadas: true, entregues: false, aCaminho: false });
  const requestRef = useRef(null);
  const arrivalCountRef = useRef({ identity: null, count: null });
  const audioContextRef = useRef(null);
  const pushIdentityRef = useRef(authenticatedIdentity);
  pushIdentityRef.current = authenticatedIdentity;
  const [pushState, setPushState] = useState(null);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushActionError, setPushActionError] = useState("");
  const carregar = useCallback(() => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    chamarDeposito("/api/deposito/painel", undefined, { signal: controller.signal })
      .then(d => {
        if (controller.signal.aborted) return;
        const quantidadeChegou = Array.isArray(d?.grupos?.agendar) ? d.grupos.agendar.length : 0;
        const anterior = arrivalCountRef.current;
        if (anterior.identity === identity && anterior.count !== null && quantidadeChegou > anterior.count) {
          if (typeof document !== "undefined") document.title = `(${quantidadeChegou}) Depósito · AR Flow`;
          try {
            const AudioContextConstructor = window.AudioContext || window.webkitAudioContext;
            if (AudioContextConstructor) {
              let audioContext = audioContextRef.current;
              if (!audioContext || audioContext.state === "closed") {
                audioContext = new AudioContextConstructor();
                audioContextRef.current = audioContext;
              }
              const oscillator = audioContext.createOscillator();
              const gain = audioContext.createGain();
              oscillator.frequency.setValueAtTime(880, audioContext.currentTime);
              gain.gain.setValueAtTime(0.15, audioContext.currentTime);
              oscillator.connect(gain);
              gain.connect(audioContext.destination);
              oscillator.start();
              oscillator.stop(audioContext.currentTime + 0.2);
              audioContext.resume?.().catch(() => {});
            }
          } catch {}
          try { navigator.vibrate?.(300); } catch {}
        }
        arrivalCountRef.current = { identity, count: quantidadeChegou };
        setResposta({ identity, dados: d, erro: "" });
      })
      .catch(e => { if (!controller.signal.aborted) setResposta(previous => ({ identity, dados: previous?.identity === identity ? previous.dados : null, erro: e.message })); })
      .finally(() => { if (requestRef.current === controller) requestRef.current = null; });
  }, [identity]);
  useEffect(() => {
    setResposta(null);
    arrivalCountRef.current = { identity, count: null };
    carregar();
    const stopPolling = watchVisiblePolling(carregar, 2 * 60 * 1000, { initial:false });
    const originalTitle = typeof document !== "undefined" ? document.title : "";
    return () => {
      stopPolling();
      requestRef.current?.abort();
      arrivalCountRef.current = { identity: null, count: null };
      if (typeof document !== "undefined") document.title = originalTitle;
      const audioContext = audioContextRef.current;
      audioContextRef.current = null;
      if (audioContext) Promise.resolve(audioContext.close?.()).catch(() => {});
    };
  }, [carregar, identity]);
  useEffect(() => {
    if (!isAuthenticatedDeposito) {
      setPushState(null);
      setPushActionError("");
      setPushBusy(false);
      return undefined;
    }
    let mounted = true;
    let supported = false;
    try {
      supported = pushSupported();
    } catch (error) {
      setPushState({ identity: authenticatedIdentity, supported: false, loading: false, active: false, error: error?.message || "Não foi possível verificar este navegador." });
      return undefined;
    }
    if (!supported) {
      setPushState({ identity: authenticatedIdentity, supported: false, loading: false, active: false, error: "" });
      return undefined;
    }
    setPushState({ identity: authenticatedIdentity, supported: true, loading: true, active: false, error: "" });
    setPushActionError("");
    getPushStatus({ storeId: 1, consultantId: 50 })
      .then(status => {
        if (!mounted || pushIdentityRef.current !== authenticatedIdentity) return;
        setPushState({
          identity: authenticatedIdentity,
          supported: status?.supported !== false,
          loading: false,
          active: status?.supported !== false && Boolean(status?.subscription) && status?.serverSaved === true,
          error: "",
        });
      })
      .catch(error => {
        if (!mounted || pushIdentityRef.current !== authenticatedIdentity) return;
        setPushState({
          identity: authenticatedIdentity,
          supported: true,
          loading: false,
          active: false,
          error: error?.message || "Não foi possível verificar os avisos neste celular.",
        });
      });
    return () => { mounted = false; };
  }, [authenticatedIdentity, isAuthenticatedDeposito]);
  const ativarAvisos = async () => {
    if (!isAuthenticatedDeposito || pushBusy) return;
    const identityAtivacao = authenticatedIdentity;
    setPushBusy(true);
    setPushActionError("");
    try {
      const status = await enablePush({ storeId: 1, consultantId: 50 });
      if (pushIdentityRef.current !== identityAtivacao) return;
      const active = status?.supported !== false && Boolean(status?.subscription) && status?.serverSaved === true;
      setPushState({
        identity: identityAtivacao,
        supported: status?.supported !== false,
        loading: false,
        active,
        error: "",
      });
      if (!active) setPushActionError("Não foi possível confirmar a ativação dos avisos. Tente novamente.");
    } catch (error) {
      if (pushIdentityRef.current === identityAtivacao) {
        setPushActionError(error?.message || "Não foi possível ativar os avisos neste celular.");
      }
    } finally {
      if (pushIdentityRef.current === identityAtivacao) setPushBusy(false);
    }
  };
  const visiblePushState = pushState?.identity === authenticatedIdentity ? pushState : null;
  const dados = resposta?.identity === identity ? resposta.dados : null;
  const erro = resposta?.identity === identity ? resposta.erro : "";
  const filtra = l => (!busca.trim() ? l : l.filter(i => `${i.cliente.nome} ${i.pedido}`.toLowerCase().includes(busca.trim().toLowerCase())));

  return (
    <div style={{ minHeight: "100vh", background: th.sand || "#FBF8F3", fontFamily: "'Source Sans 3', system-ui, sans-serif", color: th.text }}>
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "16px 16px 40px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
          <div>
            <div style={{ fontFamily: DISPLAY, fontSize: 26, fontWeight: 700 }}>📦 Depósito</div>
            <div style={{ fontSize: 14, color: th.muted }}>{dados?.usuario?.nome || currentUser?.name || ""}{dados?.lojas ? ` · ${dados.lojas.map(s => NOME_LOJA[s]).join(", ")}` : ""}</div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <button type="button" onClick={carregar} style={{ background: "none", border: `1px solid ${th.nuLine || th.sandDark}`, borderRadius: 4, padding: "6px 10px", cursor: "pointer", fontFamily: "inherit" }}>Atualizar</button>
            {onMinhaConta && <button type="button" onClick={onMinhaConta} style={{ background: "none", border: `1px solid ${th.nuLine || th.sandDark}`, borderRadius: 4, padding: "6px 10px", cursor: "pointer", fontFamily: "inherit" }}>Minha conta</button>}
            {onSair && <button type="button" onClick={onSair} style={{ background: "none", border: "none", color: th.muted, cursor: "pointer", fontFamily: "inherit" }}>Sair</button>}
          </div>
        </div>
        {isAuthenticatedDeposito && visiblePushState?.loading && <div role="status" style={{ fontSize: 13, color: th.muted, marginTop: 8 }}>Verificando avisos neste celular…</div>}
        {isAuthenticatedDeposito && visiblePushState?.supported && !visiblePushState.loading && !visiblePushState.active && (
          <button type="button" disabled={pushBusy} onClick={ativarAvisos} style={{ marginTop: 8, padding: "8px 12px", borderRadius: 4, border: `1px solid ${th.nuLine || th.sandDark}`, background: "#fff", color: th.text, fontWeight: 700, cursor: pushBusy ? "wait" : "pointer", opacity: pushBusy ? 0.6 : 1 }}>
            {pushBusy ? "Ativando avisos…" : "🔔 Ativar avisos neste celular"}
          </button>
        )}
        {isAuthenticatedDeposito && visiblePushState?.active && <div role="status" style={{ fontSize: 13, color: "#067647", marginTop: 8 }}>🔔 Avisos ativos neste celular.</div>}
        {isAuthenticatedDeposito && (visiblePushState?.error || pushActionError) && <div role="alert" style={{ color: "#991B1B", fontSize: 13, marginTop: 6 }}>{visiblePushState?.error || pushActionError}</div>}
        <input aria-label="Buscar cliente ou pedido" value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar cliente ou nº do pedido" style={{ width: "100%", boxSizing: "border-box", margin: "14px 0", padding: "10px 12px", borderRadius: 4, border: `1px solid ${th.nuLine || th.sandDark}`, fontSize: 15, fontFamily: "inherit" }} />
        {erro && <div role="alert" style={{ color: "#991B1B", marginBottom: 10 }}>{erro}</div>}
        {!dados && !erro && <div style={{ color: th.muted }}>Carregando os pedidos…</div>}
        {dados && GRUPOS.map(g => {
          const itens = filtra(dados.grupos[g.id] || []);
          return (
            <section key={g.id} style={{ marginBottom: 14 }}>
              <button type="button" onClick={() => setAberto(a => ({ ...a, [g.id]: !a[g.id] }))} aria-expanded={Boolean(aberto[g.id])}
                style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "baseline", background: "#fff", border: `1px solid ${th.nuLine || th.sandDark}`, borderTop: `3px solid ${g.cor}`, borderRadius: 4, padding: "10px 12px", cursor: "pointer", fontFamily: "inherit", color: th.text }}>
                <strong style={{ fontSize: 16 }}>{g.titulo}</strong>
                <span style={{ fontFamily: MONO, fontSize: 15, color: g.cor }}>{itens.length} {aberto[g.id] ? "▲" : "▼"}</span>
              </button>
              {aberto[g.id] && (
                <div style={{ marginTop: 8 }}>
                  {itens.length === 0 && <div style={{ fontSize: 14, color: th.muted, padding: "2px 4px" }}>{g.vazio}</div>}
                  {itens.map(item => <Card key={`${item.storeId}-${item.pedido}`} th={th} item={item} meta={{ ...dados, grupo: g.id }} onFeito={carregar} />)}
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}